import { useState, useEffect, useRef } from "react";
import { X, Star, Tag } from "lucide-react";
import { ImageUpload } from "./ImageUpload";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

// ISO -> valeur <input type="datetime-local"> (heure locale, à la minute)
const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToIso = (v) => (v ? new Date(v).toISOString() : null);

// Éditeur d'un article existant : infos, photo, recommandation, promotion.
export const PartnerItemEditor = ({ item, onClose, onSave, categories = [], onAssignCategories, focusPromo = false }) => {
  const p = item.promo || {};
  const initialCatIds = (item.category_ids && item.category_ids.length ? item.category_ids : [item.category_id]).filter(Boolean);
  const [catIds, setCatIds] = useState(initialCatIds);
  const [form, setForm] = useState({
    name: item.name || "",
    subtitle: item.subtitle || "",
    size_label: item.size_label || "",
    description: item.description || "",
    price: String(item.original_price ?? item.price ?? ""),
    image_url: item.image_url || "",
    recommended: !!item.recommended,
    recommended_note: item.recommended_note || "",
    promo_enabled: !!p.enabled,
    promo_kind: p.kind || "percent",
    promo_percent: p.percent != null ? String(p.percent) : "",
    promo_amount: p.amount != null ? String(p.amount) : "",
    promo_fixed: p.fixed_price != null ? String(p.fixed_price) : "",
    promo_start: isoToLocal(p.start),
    promo_end: isoToLocal(p.end),
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const promoRef = useRef(null);

  // Raccourci « Promo » : ouvre l'éditeur, active la promo et défile jusqu'à la section.
  useEffect(() => {
    if (!focusPromo) return;
    setForm((f) => ({ ...f, promo_enabled: true }));
    const t = setTimeout(() => {
      promoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => clearTimeout(t);
  }, [focusPromo]);

  const base = parseFloat(String(form.price).replace(",", ".")) || 0;
  const pct = parseFloat(String(form.promo_percent).replace(",", ".")) || 0;
  const amt = parseFloat(String(form.promo_amount).replace(",", ".")) || 0;
  const fixed = parseFloat(String(form.promo_fixed).replace(",", ".")) || 0;
  const computeEff = () => {
    if (form.promo_kind === "percent") return pct > 0 ? Math.round(base * (1 - pct / 100) * 100) / 100 : null;
    if (form.promo_kind === "amount_off") return amt > 0 ? Math.round(Math.max(0, base - amt) * 100) / 100 : null;
    if (form.promo_kind === "fixed_price") return form.promo_fixed !== "" ? Math.round(fixed * 100) / 100 : null;
    return null; // formules (second_50/bogo) : prix unitaire inchangé
  };
  const preview = form.promo_enabled ? computeEff() : null;
  const PROMO_KINDS = [
    { k: "percent", label: "% réduction" },
    { k: "amount_off", label: "CHF réduction" },
    { k: "second_50", label: "2ᵉ à -50%" },
    { k: "bogo", label: "1 acheté = 1 offert" },
  ];

  const save = async () => {
    const price = parseFloat(String(form.price).replace(",", "."));
    if (!form.name.trim() || isNaN(price) || price < 0) return;
    if (catIds.length === 0) return;
    setBusy(true);
    try {
      const sortedNew = [...catIds].sort().join(",");
      const sortedOld = [...initialCatIds].sort().join(",");
      if (onAssignCategories && sortedNew !== sortedOld) {
        await onAssignCategories(catIds);
      }
      await onSave({
        name: form.name.trim(),
        subtitle: form.subtitle.trim(),
        size_label: form.size_label.trim(),
        description: form.description.trim(),
        price,
        image_url: form.image_url.trim(),
        recommended: form.recommended,
        recommended_note: form.recommended_note.trim(),
        promo: {
          enabled: form.promo_enabled,
          kind: form.promo_kind,
          percent: pct,
          amount: amt,
          fixed_price: form.promo_fixed !== "" ? fixed : null,
          start: localToIso(form.promo_start),
          end: localToIso(form.promo_end),
        },
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(2,6,23,.55)" }} data-testid="item-editor-overlay">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="item-editor">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#eef0ef" }}>
          <div>
            <p className="text-xs font-bold uppercase" style={{ color: "#94a3b8" }}>Modifier l&apos;article</p>
            <p className="font-extrabold" style={{ color: GREEN }}>{item.name}</p>
          </div>
          <button data-testid="item-editor-close" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#f1f5f9" }}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {form.image_url && <img src={form.image_url} alt="" className="w-full h-40 rounded-2xl object-cover" />}
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Nom</label>
          <input data-testid="ie-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Nom du produit" className="w-full rounded-xl px-4 h-11 text-sm outline-none" style={field} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="ie-subtitle" value={form.subtitle} onChange={(e) => set({ subtitle: e.target.value })} placeholder="Sous-titre" className="rounded-xl px-4 h-11 text-sm outline-none" style={field} />
            <input data-testid="ie-size" value={form.size_label} onChange={(e) => set({ size_label: e.target.value })} placeholder="Contenance (ex : 70 cl)" className="rounded-xl px-4 h-11 text-sm outline-none" style={field} />
          </div>
          <textarea data-testid="ie-desc" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Description" rows={2} className="w-full rounded-xl px-4 py-2 text-sm outline-none" style={field} />
          <div>
            <label className="text-xs font-bold" style={{ color: "#64748b" }}>Photo</label>
            <div className="mt-1"><ImageUpload testid="ie-photo" value={form.image_url} onChange={(url) => set({ image_url: url })} /></div>
          </div>
          <input data-testid="ie-image" value={form.image_url} onChange={(e) => set({ image_url: e.target.value })} placeholder="ou coller une URL (https://…)" className="w-full rounded-xl px-4 h-11 text-sm outline-none" style={field} />
          <div>
            <label className="text-xs font-bold" style={{ color: "#64748b" }}>Prix (CHF)</label>
            <input data-testid="ie-price" value={form.price} onChange={(e) => set({ price: e.target.value })} inputMode="decimal" placeholder="Prix" className="w-full rounded-xl px-4 h-11 text-sm outline-none" style={field} />
          </div>

          {/* Sous-rubriques (produit PARTAGÉ : cochez plusieurs rubriques sans dupliquer) */}
          {categories.length > 0 && (
            <div className="rounded-2xl p-3" style={{ border: `1px solid ${GREEN}22`, background: "#f6fbf9" }}>
              <p className="flex items-center gap-2 font-bold text-sm mb-2" style={{ color: GREEN }}>Sous-rubriques</p>
              <p className="text-[11px] mb-2" style={{ color: "#64748b" }}>Cochez une ou plusieurs rubriques. Le même produit apparaît partout, sans double saisie.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="ie-categories">
                {categories.map((c) => {
                  const on = catIds.includes(c.id);
                  return (
                    <button key={c.id} type="button" data-testid={`ie-cat-${c.id}`}
                      onClick={() => setCatIds((ids) => on ? ids.filter((x) => x !== c.id) : [...ids, c.id])}
                      className="text-xs font-bold px-3 h-9 rounded-full"
                      style={on ? { background: GREEN, color: "#fff" } : { background: "#fff", color: "#64748b", border: "1px solid #e5e7eb" }}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              {catIds.length === 0 && <p className="text-[11px] mt-1" style={{ color: "#dc2626" }}>Sélectionnez au moins une sous-rubrique.</p>}
            </div>
          )}

          {/* Recommandation client */}
          <div className="rounded-2xl p-3" style={{ border: `1px solid ${GOLD}33`, background: "#fffdf5" }}>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="flex items-center gap-2 font-bold text-sm" style={{ color: GOLD }}><Star size={16} /> Recommandé par le chef</span>
              <input type="checkbox" data-testid="ie-recommended" checked={form.recommended} onChange={(e) => set({ recommended: e.target.checked })} />
            </label>
            {form.recommended && (
              <input data-testid="ie-recommended-note" value={form.recommended_note} onChange={(e) => set({ recommended_note: e.target.value })} placeholder="Note (ex : Le coup de cœur du chef)" className="w-full rounded-xl px-4 h-10 text-sm outline-none mt-2" style={field} />
            )}
          </div>

          {/* Promotion */}
          <div ref={promoRef} className="rounded-2xl p-3" style={{ border: `1px solid ${ORANGE}33`, background: "#fff7f3" }}>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="flex items-center gap-2 font-bold text-sm" style={{ color: ORANGE }}><Tag size={16} /> Promotion</span>
              <input type="checkbox" data-testid="ie-promo-enabled" checked={form.promo_enabled} onChange={(e) => set({ promo_enabled: e.target.checked })} />
            </label>
            {form.promo_enabled && (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-1.5" data-testid="ie-promo-kinds">
                  {PROMO_KINDS.map((o) => (
                    <button key={o.k} type="button" data-testid={`ie-promo-kind-${o.k}`}
                      onClick={() => set({ promo_kind: o.k })}
                      className="text-xs font-bold px-3 h-9 rounded-full"
                      style={form.promo_kind === o.k
                        ? { background: ORANGE, color: "#fff" }
                        : { background: "#fff", color: "#64748b", border: "1px solid #e5e7eb" }}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {form.promo_kind === "percent" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: "#64748b" }}>Réduction</span>
                    <input data-testid="ie-promo-percent" value={form.promo_percent} onChange={(e) => set({ promo_percent: e.target.value })} inputMode="decimal" placeholder="%" className="w-20 rounded-lg px-2 h-10 text-sm outline-none" style={field} />
                    <span className="text-xs" style={{ color: "#64748b" }}>%</span>
                  </div>
                )}
                {form.promo_kind === "amount_off" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: "#64748b" }}>Réduction</span>
                    <input data-testid="ie-promo-amount" value={form.promo_amount} onChange={(e) => set({ promo_amount: e.target.value })} inputMode="decimal" placeholder="CHF" className="w-24 rounded-lg px-2 h-10 text-sm outline-none" style={field} />
                    <span className="text-xs" style={{ color: "#64748b" }}>CHF</span>
                  </div>
                )}
                {form.promo_kind === "fixed_price" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: "#64748b" }}>Prix promo</span>
                    <input data-testid="ie-promo-fixed" value={form.promo_fixed} onChange={(e) => set({ promo_fixed: e.target.value })} inputMode="decimal" placeholder="CHF" className="w-24 rounded-lg px-2 h-10 text-sm outline-none" style={field} />
                    <span className="text-xs" style={{ color: "#64748b" }}>CHF</span>
                  </div>
                )}
                {(form.promo_kind === "second_50" || form.promo_kind === "bogo") && (
                  <p className="text-[11px]" style={{ color: "#64748b" }}>Formule appliquée automatiquement au panier. Le prix unitaire reste inchangé ; un badge s&apos;affiche côté client.</p>
                )}
                {preview != null && (
                  <span className="block text-sm font-extrabold" data-testid="ie-promo-preview">
                    <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>CHF {base.toFixed(2)}</span>{" "}
                    <span style={{ color: ORANGE }}>CHF {preview.toFixed(2)}</span>
                  </span>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-bold" style={{ color: "#94a3b8" }}>Début (optionnel)</label>
                    <input type="datetime-local" data-testid="ie-promo-start" value={form.promo_start} onChange={(e) => set({ promo_start: e.target.value })} className="w-full rounded-lg px-2 h-10 text-sm outline-none" style={field} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold" style={{ color: "#94a3b8" }}>Fin (optionnel)</label>
                    <input type="datetime-local" data-testid="ie-promo-end" value={form.promo_end} onChange={(e) => set({ promo_end: e.target.value })} className="w-full rounded-lg px-2 h-10 text-sm outline-none" style={field} />
                  </div>
                </div>
                <p className="text-[11px]" style={{ color: "#94a3b8" }}>Sans dates, la promo s&apos;applique immédiatement tant qu&apos;elle est activée.</p>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex gap-2 px-5 py-4 border-t" style={{ borderColor: "#eef0ef" }}>
          <button data-testid="item-editor-cancel" onClick={onClose} className="flex-1 rounded-xl font-bold h-11" style={{ background: "#f1f5f9", color: "#334155" }}>Annuler</button>
          <button data-testid="item-editor-save" onClick={save} disabled={busy} className="flex-1 rounded-xl font-bold text-white h-11" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>{busy ? "…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
};
